import {
  useDashboardPathname as usePathname,
  useDashboardInstanceSlug,
} from "@/hooks/useDashboardNavigation";
import {
  defaultOrgChartFilters as defaultDockerOrgChartFilters,
  defaultRepoChartFilters as defaultDockerRepoChartFilters,
  dockerOrgChartFiltersAtom,
  dockerOrgTableFiltersAtom,
  dockerRepoChartFiltersAtom,
  dockerRepoTableFiltersAtom,
} from "@/atoms/dockerFilterAtoms";
import {
  defaultOrgChartFilters,
  defaultRepoChartFilters,
  ghaOrgChartFiltersAtom,
  ghaOrgRepoTableFiltersAtom,
  ghaRepoChartFiltersAtom,
  ghaRepoJobTableFiltersAtom,
} from "@/atoms/ghaFilterAtoms";
import { defaultOrgFilters, runsOrgFiltersAtom } from "@/atoms/runsFilterAtoms";
import { AuthContext, useSuspenseUser } from "@/components/AuthProvider";
import { assert, assertDefined } from "@/lib/assertions";
import { ASSISTANT_SESSIONS_QUERY_KEY } from "@/lib/assistant-sessions";
import {
  DEFAULT_GITHUB_INSTANCE_SLUG,
  type NamedOrg,
  orgApiPath,
  urlInstanceSlug,
} from "@/lib/named-org";
import { splitInstanceBasePath } from "@/lib/dashboard-routes";
import { identifyUserForOrg } from "@/lib/posthog-identify";
import axios, { type AxiosError, type AxiosInstance } from "axios";
import { atom, useAtom } from "jotai";
import { DateTime } from "luxon";
import {
  dashboardRouteParams,
  useDashboardRouter as usePageRouter,
} from "@/hooks/useDashboardRouter";
import { usePostHog } from "posthog-js/react";
import { useContext, useMemo, useEffect } from "react";
import type { DateRange } from "react-day-picker";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { toast } from "sonner";
import * as z from "zod";
import { useApi } from "./useApi";

export interface GitHubRateLimitErrorResponse {
  error_type: "github_rate_limit";
  message: string;
  rate_limit_reset: number;
  rate_limit_reset_seconds: number | null;
}

function isGitHubRateLimitError(
  data: unknown,
): data is GitHubRateLimitErrorResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "error_type" in data &&
    (data as GitHubRateLimitErrorResponse).error_type === "github_rate_limit"
  );
}

export class GitHubRateLimitException extends Error {
  public readonly rateLimitReset: number;
  public readonly rateLimitResetSeconds: number | null;

  constructor(data: GitHubRateLimitErrorResponse) {
    super(data.message);
    this.name = "GitHubRateLimitException";
    this.rateLimitReset = data.rate_limit_reset;
    this.rateLimitResetSeconds = data.rate_limit_reset_seconds;
  }
}

// GitHub SAML SSO re-authorization error types. The backend returns this when
// GitHub hid every org the user has from /user/installations because their
// OAuth token lost its SAML SSO authorization (e.g. after an SSO change).
export interface GithubSsoReauthorizationRequiredResponse {
  error_type: "github_sso_reauthorization_required";
  message: string;
  sso_blocked_orgs: SsoBlockedOrg[];
}

function isGithubSsoReauthorizationRequiredError(
  data: unknown,
): data is GithubSsoReauthorizationRequiredResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "error_type" in data &&
    (data as GithubSsoReauthorizationRequiredResponse).error_type ===
      "github_sso_reauthorization_required"
  );
}

export class GithubSsoReauthorizationRequiredException extends Error {
  public readonly ssoBlockedOrgs: SsoBlockedOrg[];

  constructor(data: GithubSsoReauthorizationRequiredResponse) {
    super(data.message);
    this.name = "GithubSsoReauthorizationRequiredException";
    this.ssoBlockedOrgs = data.sso_blocked_orgs ?? [];
  }
}

/** Only fields the UI reads: this endpoint returns many installations and Zod parsing blocks the main thread. */
const Installation = z.object({
  id: z.number(),
  account: z.object({
    login: z.string(),
    avatar_url: z.string(),
    type: z.string(),
  }),
  created_at: z.string(),
  suspended_by: z.any().nullable(),
  suspended_at: z.string().nullable(),
  has_payment_method: z.boolean(),
  blocked: z.boolean().optional(),
  has_run_first_job: z.boolean().optional(),
  has_run_incremental_docker_build: z.boolean().optional(),
  hit_secondary_rate_limit: z.boolean().optional(),
  next_scale_app: z
    .object({
      name: z.string(),
      install_url: z.string(),
    })
    .nullable()
    .optional(),
  enable_multi_repo_migration_wizard: z.boolean().optional(),
  codesmith_enabled: z.boolean().optional(),
  codesmith_needs_organization: z.boolean().optional(),
  codesmith_automations_enabled: z.boolean().optional(),
  codesmith_projects_enabled: z.boolean().optional(),
  codesmith_vm_debug_ssh_enabled: z.boolean().optional(),
  codesmith_pr_ready_dms_enabled: z.boolean().optional(),
  codesmith_left_sidebar_redesign_enabled: z.boolean().optional(),
  codesmith_session_search_enabled: z.boolean().optional(),
  codesmith_review_rollout_enabled: z.boolean().optional(),
  codesmith_slack_dm_rollout_enabled: z.boolean().optional(),
  proof_enabled: z.boolean().optional(),
  codesmith_proof_recording_chapters_enabled: z.boolean().optional(),
  codesmith_proof_settings_enabled: z.boolean().optional(),
  autofix_user_default_available: z.boolean().optional(),
  bazel_cache_dashboard_enabled: z.boolean().optional(),
  network_dashboard_enabled: z.boolean().optional(),
  global_network_monitoring_enabled: z.boolean().optional(),
  viewed_control_plane_static_ips: z.boolean().optional(),
  stickydisk_cache_allowance_gb: z.number().optional(),
  repos_without_access: z.array(z.string()).nullable().optional(),
  blacksmith_id: z.number().optional(),
});

const DailyMetricsItem = z.object({
  date: z.string(),
  jobs: z.number(),
  cost: z.number(),
  minutes: z.number(),
});

const DailyMetrics = z.object({
  daily_metrics: z.array(DailyMetricsItem),
});

export type DailyMetrics = z.infer<typeof DailyMetrics>;

export type DailyMetricsItem = z.infer<typeof DailyMetricsItem>;

const TotalMetrics = z.object({
  total_jobs: z.number(),
  total_cost: z.number(),
  total_minutes: z.number(),
});

export type TotalMetrics = z.infer<typeof TotalMetrics>;

// Orgs GitHub excluded from the user's installations because their OAuth
// token lacks an active SAML SSO authorization. The sso_url is GitHub's
// one-click flow to re-authorize the token for that org.
const SsoBlockedOrg = z.object({
  name: z.string(),
  sso_url: z.string(),
});

export type SsoBlockedOrg = z.infer<typeof SsoBlockedOrg>;

const GithubInstallations = z.object({
  total_count: z.number(),
  active_org_name: z.string().nullable(),
  active_instance: z.string().nullable().optional(),
  // Self-hosted GitHub Enterprise Server; false for github.com and GHEC data residency.
  instance_is_enterprise_server: z.boolean().optional(),
  is_org_admin: z.boolean().optional(),
  installations: z.array(Installation),
  sso_blocked_orgs: z.array(SsoBlockedOrg).optional(),
});

export type GithubInstallations = Omit<
  z.infer<typeof GithubInstallations>,
  "installations"
> & {
  instance_slug: string;
  installations: (z.infer<typeof Installation> & {
    instance_slug: string;
  })[];
};

export function orgInstanceSlug(
  orgs: GithubInstallations | undefined,
  orgName: string | null,
  isSuperuser = false,
): string | null {
  if (!orgs || !orgName) return null;
  const installation = orgs.installations.find(
    (candidate) =>
      candidate.account.login.toLowerCase() === orgName.toLowerCase(),
  );
  return (
    installation?.instance_slug ?? (isSuperuser ? urlInstanceSlug() : null)
  );
}

// github.com keys stay [name, org] so existing seeds and prefix invalidations still match.
function orgQueryKey(name: string, org: NamedOrg | null) {
  return !org || org.instanceSlug === DEFAULT_GITHUB_INSTANCE_SLUG
    ? [name, org?.orgName ?? null]
    : [name, org.orgName, org.instanceSlug];
}

function computeIsImpersonating(
  user: { is_superuser: boolean } | null | undefined,
  installations: GithubInstallations["installations"] | undefined,
  effectiveOrg: string | null,
): boolean {
  if (!user?.is_superuser) return false;
  if (!installations || !effectiveOrg) return false;
  return !installations.some(
    (installation) => installation.account.login === effectiveOrg,
  );
}

// The backend serves /user/github/orgs from a cached snapshot; callers that just
// changed what GitHub returns (SSO re-authorization, a new install) skip it until
// a live fetch succeeds, so React Query's retry of a failed one skips it too.
let bypassOrgsSnapshot = false;

const SWITCH_ACTIVE_ORG_MUTATION_KEY = "switchActiveOrg";

export function connectedGithubOrgsQueryKey(instanceSlug = urlInstanceSlug()) {
  return instanceSlug === DEFAULT_GITHUB_INSTANCE_SLUG
    ? "connectedGithubOrgs"
    : ["connectedGithubOrgs", instanceSlug];
}

export function invalidateConnectedGithubOrgs(
  queryClient: QueryClient,
  instanceSlug?: string,
) {
  bypassOrgsSnapshot = true;
  return queryClient.invalidateQueries(
    connectedGithubOrgsQueryKey(instanceSlug),
  );
}

// GitHub appends installation_id/setup_action when redirecting from the app's setup URL.
function landedFromGithubAppSetup() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("setup_action") || params.has("installation_id");
}

function urlOrgName() {
  if (typeof window === "undefined") return undefined;
  // Mirrors useOrgUrlSync: an internal-monitors URL names the monitored org, not the user's.
  const { pathname } = splitInstanceBasePath(window.location.pathname);
  if (pathname.startsWith("/admin/internal-monitors")) return undefined;
  const org = dashboardRouteParams(pathname)?.org;
  return typeof org === "string" ? org : undefined;
}

// Exported so it can be used for prefetching in AuthProvider
export async function fetchOrgs(
  api: AxiosInstance,
  instanceSlug = urlInstanceSlug(),
) {
  const bypassSnapshot = bypassOrgsSnapshot || landedFromGithubAppSetup();
  try {
    const response = await api.get(
      `/user/${encodeURIComponent(instanceSlug)}/orgs`,
      { params: bypassSnapshot ? { refresh: 1 } : undefined },
    );
    bypassOrgsSnapshot = false;
    const parsed = GithubInstallations.parse(response.data);
    const installations: GithubInstallations = {
      ...parsed,
      instance_slug: instanceSlug,
      installations: parsed.installations.map((installation) => ({
        ...installation,
        instance_slug: instanceSlug,
      })),
    };
    // An active org on another instance is not this listing's: activate one of its own.
    const activeOrgIsListed =
      installations.active_org_name !== null &&
      (installations.active_instance ?? installations.instance_slug) ===
        installations.instance_slug;
    if (activeOrgIsListed) return installations;
    const urlOrg = urlOrgName()?.toLowerCase();
    const fallbackInstallation =
      installations.installations.find(
        (installation) => installation.account.login.toLowerCase() === urlOrg,
      ) ?? installations.installations[0];
    if (!fallbackInstallation) {
      return { ...installations, active_org_name: null, active_instance: null };
    }
    // A failed activation fails the query so React Query retries it; nothing else retries it on pages without an org in the URL.
    const activation = await api.post(
      `/user/${encodeURIComponent(fallbackInstallation.instance_slug)}/orgs`,
      { org_name: fallbackInstallation.account.login },
    );
    return {
      ...installations,
      active_org_name: fallbackInstallation.account.login,
      active_instance: fallbackInstallation.instance_slug,
      is_org_admin: activation.data?.is_org_admin,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 429) {
      const data = error.response.data;
      if (isGitHubRateLimitError(data)) {
        throw new GitHubRateLimitException(data);
      }
    }
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      const data = error.response.data;
      if (isGithubSsoReauthorizationRequiredError(data)) {
        throw new GithubSsoReauthorizationRequiredException(data);
      }
    }
    throw error;
  }
}

// Also seeds the hasPaymentMethod query cache for every org.
export function useConnectedGithubOrgs() {
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const user = useSuspenseUser();

  const scheduleConnectedOrgSideEffects = (data: GithubInstallations) => {
    // These side effects can be surprisingly expensive when a user has many orgs.
    // Run them in a separate task so we don't block initial rendering/spinners.
    const run = () => {
      if (!data.active_org_name) {
        assert(
          data.installations.length === 0,
          "No active org name found but the list of installations is not empty",
        );
      }
      if (data.active_org_name) {
        identifyUserForOrg(posthog, user, data.active_org_name);
      }
      for (const installation of data.installations) {
        queryClient.setQueryData(
          orgQueryKey("hasPaymentMethod", {
            orgName: installation.account.login,
            instanceSlug: installation.instance_slug,
          }),
          installation.has_payment_method,
        );
      }
    };

    if (typeof window === "undefined") {
      run();
      return;
    }
    if ("requestIdleCallback" in window) {
      (window as Window).requestIdleCallback(run, { timeout: 100 });
    } else {
      setTimeout(run, 0);
    }
  };

  const instanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  const { data } = useQuery(
    connectedGithubOrgsQueryKey(instanceSlug),
    () => fetchOrgs(api, instanceSlug),
    {
      enabled: !!user,
      suspense: true,
      // No background refetches: a stale response can overwrite a just-switched
      // active org, making the dropdown appear to snap back.
      staleTime: Number.POSITIVE_INFINITY,
      onSuccess: (data) => {
        scheduleConnectedOrgSideEffects(data);
      },
    },
  );

  return {
    connectedGithubOrgs: data,
  };
}

export function useConnectedGithubOrgsNonSuspense() {
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { user } = useContext(AuthContext);

  const scheduleConnectedOrgSideEffects = (data: GithubInstallations) => {
    // Same deferred execution logic as the suspense version
    const run = () => {
      if (!data.active_org_name) {
        assert(
          data.installations.length === 0,
          "No active org name found but the list of installations is not empty",
        );
      }
      if (data.active_org_name) {
        identifyUserForOrg(posthog, user, data.active_org_name);
      }
      for (const installation of data.installations) {
        queryClient.setQueryData(
          orgQueryKey("hasPaymentMethod", {
            orgName: installation.account.login,
            instanceSlug: installation.instance_slug,
          }),
          installation.has_payment_method,
        );
      }
    };

    if (typeof window === "undefined") {
      run();
      return;
    }
    if ("requestIdleCallback" in window) {
      (window as Window).requestIdleCallback(run, { timeout: 100 });
    } else {
      setTimeout(run, 0);
    }
  };

  const instanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  const { data, isLoading, error } = useQuery(
    connectedGithubOrgsQueryKey(instanceSlug),
    () => fetchOrgs(api, instanceSlug),
    {
      enabled: !!user,
      suspense: false,
      staleTime: Number.POSITIVE_INFINITY,
      onSuccess: (data) => {
        scheduleConnectedOrgSideEffects(data);
      },
    },
  );

  return {
    connectedGithubOrgs: data,
    // True only while the initial fetch is in flight. Unlike checking
    // `!data`, this resolves to false when the query errors, so callers
    // gating loading UI on it don't show skeletons forever on failure.
    isLoading,
    error,
  };
}

// Gated on is_superuser so a regular user navigating to a foreign org URL
// doesn't get impersonation indicators.
export function useIsImpersonatingNonSuspense() {
  const { user } = useContext(AuthContext);
  const pageRouter = usePageRouter();
  const { connectedGithubOrgs } = useConnectedGithubOrgsNonSuspense();

  const urlOrg = pageRouter.query.org as string | undefined;
  const effectiveOrg = urlOrg ?? connectedGithubOrgs?.active_org_name ?? null;

  return computeIsImpersonating(
    user,
    connectedGithubOrgs?.installations,
    effectiveOrg,
  );
}

export function useIsOrgAdmin() {
  const { isOrgAdmin } = useActiveGithubOrgForUser();
  return isOrgAdmin;
}

export function useActiveGithubOrgForUser() {
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const user = useSuspenseUser();
  const pageRouter = usePageRouter();
  const currentPath = usePathname();
  const { connectedGithubOrgs } = useConnectedGithubOrgs();
  const orgsQueryKey = connectedGithubOrgsQueryKey(
    connectedGithubOrgs?.instance_slug ??
      urlInstanceSlug(undefined, user?.default_instance_slug),
  );

  const queryOrg = pageRouter.query.org;
  const urlOrg = typeof queryOrg === "string" ? queryOrg : undefined;

  const [, setGHAOrgChartFilters] = useAtom(ghaOrgChartFiltersAtom);
  const [, setGHARepoChartFilters] = useAtom(ghaRepoChartFiltersAtom);
  const [, setGHAOrgRepoTableFilters] = useAtom(ghaOrgRepoTableFiltersAtom);
  const [, setGHARepoJobTableFilters] = useAtom(ghaRepoJobTableFiltersAtom);

  const [, setDockerOrgChartFilters] = useAtom(dockerOrgChartFiltersAtom);
  const [, setDockerRepoChartFilters] = useAtom(dockerRepoChartFiltersAtom);
  const [, setDockerOrgTableFilters] = useAtom(dockerOrgTableFiltersAtom);
  const [, setDockerRepoTableFilters] = useAtom(dockerRepoTableFiltersAtom);

  const [, setRunsOrgFilters] = useAtom(runsOrgFiltersAtom);

  const data = urlOrg ?? connectedGithubOrgs?.active_org_name ?? null;

  const { mutate } = useMutation(
    async (orgName: string) => {
      const instanceSlug = orgInstanceSlug(
        connectedGithubOrgs,
        orgName,
        user?.is_superuser,
      );
      if (!instanceSlug) throw new Error("GitHub instance is not resolved");
      const response = await api.post(
        `/user/${encodeURIComponent(instanceSlug)}/orgs`,
        {
          org_name: orgName,
        },
      );
      return response.data;
    },
    {
      mutationKey: SWITCH_ACTIVE_ORG_MUTATION_KEY,
      onMutate: async (orgName) => {
        await queryClient.cancelQueries(orgsQueryKey);
        assertDefined(
          connectedGithubOrgs,
          "GitHub organizations data is undefined during org switch",
        );

        queryClient.setQueryData(orgsQueryKey, {
          ...(connectedGithubOrgs ?? { installations: [] }),
          active_org_name: orgName,
          // The optimistic update can't know the new org's is_org_admin; onSuccess corrects it.
          is_org_admin: connectedGithubOrgs?.is_org_admin ?? false,
        });

        identifyUserForOrg(posthog, user, orgName);

        setGHAOrgChartFilters(defaultOrgChartFilters);
        setGHARepoChartFilters(defaultRepoChartFilters);
        setGHAOrgRepoTableFilters({});
        setGHARepoJobTableFilters({});

        setDockerOrgChartFilters(defaultDockerOrgChartFilters);
        setDockerRepoChartFilters(defaultDockerRepoChartFilters);
        setDockerOrgTableFilters({});
        setDockerRepoTableFilters({});

        setRunsOrgFilters(defaultOrgFilters);
      },
      onSuccess: async (data) => {
        // Corrects the is_org_admin the optimistic update in onMutate had to guess.
        queryClient.setQueryData(orgsQueryKey, (prev: any) => ({
          ...(prev ?? { installations: [] }),
          active_org_name: data.active_org_name,
          active_instance: data.active_instance,
          is_org_admin: data.is_org_admin,
        }));
        // /api/user resolves active-org fields, e.g. the combined PR workspace.
        queryClient.invalidateQueries("user");
      },
      onError: (err, orgName) => {
        toast.error("Failed to set active org");
      },
    },
  );

  const isImpersonating = computeIsImpersonating(
    user,
    connectedGithubOrgs?.installations,
    data,
  );

  const isOrgAdmin = connectedGithubOrgs?.is_org_admin ?? false;
  const instanceSlug = orgInstanceSlug(
    connectedGithubOrgs,
    data,
    user?.is_superuser,
  );

  const activeNamedOrg = useMemo<NamedOrg | null>(
    () => (data && instanceSlug ? { orgName: data, instanceSlug } : null),
    [data, instanceSlug],
  );

  return {
    connectedGithubOrgsLoaded: connectedGithubOrgs !== undefined,
    activeNamedOrg,
    activeGithubOrgIsEnterpriseServer:
      connectedGithubOrgs?.instance_is_enterprise_server ?? false,
    setActiveGithubOrg: mutate,
    isOrgAdmin,
    hasViewedControlPlaneStaticIps:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.viewed_control_plane_static_ips ?? false,
    activeGithubOrgBlocked:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.blocked ?? false,
    activeGithubOrgHasRunFirstJob:
      (isImpersonating ||
        connectedGithubOrgs?.installations.find(
          (installation) => installation?.account?.login === data,
        )?.has_run_first_job) ??
      false,
    activeGithubOrgHasRunIncrementalDockerBuild:
      (isImpersonating ||
        connectedGithubOrgs?.installations.find(
          (installation) => installation?.account?.login === data,
        )?.has_run_incremental_docker_build) ??
      false,
    bazelCacheDashboardEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.bazel_cache_dashboard_enabled ??
        false),
    networkDashboardEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.network_dashboard_enabled ??
        false),
    globalNetworkMonitoringEnabled:
      user?.is_superuser === true ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.global_network_monitoring_enabled ??
        false),
    multiRepoMigrationWizardEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.enable_multi_repo_migration_wizard ??
        false),
    // codesmith_enabled is the resolved value from the backend
    // (GithubAppInstallation::isCodesmithEnabled), which folds in suspended
    // state but deliberately not `blocked`: blocked orgs still see the
    // Codesmith UI, and usage is gated via activeGithubOrgBlocked +
    // backend admission.
    codesmithEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_enabled ??
        false),
    // codesmithEnabled falls back to false when org data is missing (query
    // error, or the active org isn't in installations), so callers that must
    // distinguish "off" from "unknown" (e.g. the canvas viewer's redirect)
    // check this alongside it.
    codesmithEnabledKnown:
      isImpersonating ||
      connectedGithubOrgs?.installations.some(
        (installation) => installation?.account?.login === data,
      ) === true,
    // GithubAppInstallation::codesmithNeedsOrganization: a personal account
    // that sees [code]smith's Home tile and "needs an organization" page
    // while codesmithEnabled stays false.
    codesmithNeedsOrganization:
      connectedGithubOrgs?.installations.find(
        (installation) =>
          installation.account.login.toLowerCase() === data?.toLowerCase(),
      )?.codesmith_needs_organization ?? false,
    // codesmith_automations_enabled is the resolved value from the backend
    // (GithubAppInstallation::isCodesmithAutomationsEnabled).
    codesmithAutomationsEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_automations_enabled ??
        false),
    // codesmith_projects_enabled is the resolved value from the backend
    // (GithubAppInstallation::isCodesmithProjectsEnabled). No impersonation
    // bypass: the projects API resolves the rollout for the impersonating
    // viewer and 404s when the org is not enrolled.
    codesmithProjectsEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_projects_enabled ?? false,
    // Gates the monitor "Fix with Codesmith" button. Same flag that gates
    // backend per-VM key provisioning, so the button only appears when the
    // SSH-debug feature is actually live for the org.
    vmDebugSshEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_vm_debug_ssh_enabled ??
        false),
    // Gates the PR-ready Slack DM feature (the per-user toggle and the
    // welcome DM). Backend mirrors the gate everywhere a DM could fire.
    codesmithPrReadyDmsEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_pr_ready_dms_enabled ??
        false),
    codesmithLeftSidebarRedesignEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_left_sidebar_redesign_enabled ?? false,
    // Gates the Review sub-tab. Same rollout the backend 404s the review
    // settings endpoints on; the org-admin mode underneath it lives in
    // codesmith-settings as codesmith_code_review.
    codesmithReviewRolloutEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_review_rollout_enabled ??
        false),
    codesmithSlackDmRolloutEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_slack_dm_rollout_enabled ??
        false),
    // No impersonation bypass: the proofs listing gates on the org rollout.
    proofEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.proof_enabled ?? false,
    proofRecordingChaptersEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_proof_recording_chapters_enabled ?? false,
    codesmithProofSettingsEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_proof_settings_enabled ??
        false),
    // Grandfathered per-user autofix default. Read here, not from
    // user-autofix-settings, so the settings rows render on first paint. A
    // listed org whose payload predates the flag keeps the setting, so a
    // dashboard-first deploy changes nothing.
    autofixUserDefaultAvailable:
      connectedGithubOrgs?.installations.some(
        (installation) =>
          installation?.account?.login === data &&
          installation.autofix_user_default_available !== false,
      ) === true,
    activeGithubOrgHitSecondaryRateLimit:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.hit_secondary_rate_limit ?? false,
    activeGithubOrgNextScaleApp:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.next_scale_app ?? null,
    activeGithubOrgReposWithoutAccess:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.repos_without_access ?? null,
    stickyDiskCacheAllowanceGb:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.stickydisk_cache_allowance_gb ?? null,
    activeGithubOrgCreatedAt:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.created_at ?? null,
  };
}

// Non-suspense version of useActiveGithubOrgForUser for components that can render
// without org data (e.g., Sidebar). Uses URL org immediately when available.
export function useActiveGithubOrgForUserNonSuspense() {
  const { user } = useContext(AuthContext);
  const pageRouter = usePageRouter();
  const {
    connectedGithubOrgs,
    isLoading,
    error: orgsError,
  } = useConnectedGithubOrgsNonSuspense();

  const queryOrg = pageRouter.query.org;
  const urlOrg = typeof queryOrg === "string" ? queryOrg : undefined;

  const data = urlOrg ?? connectedGithubOrgs?.active_org_name ?? null;

  const isImpersonating = computeIsImpersonating(
    user,
    connectedGithubOrgs?.installations,
    data,
  );

  const isOrgAdmin = connectedGithubOrgs?.is_org_admin ?? false;
  const instanceSlug = orgInstanceSlug(
    connectedGithubOrgs,
    data,
    user?.is_superuser,
  );

  const activeNamedOrg = useMemo<NamedOrg | null>(
    () => (data && instanceSlug ? { orgName: data, instanceSlug } : null),
    [data, instanceSlug],
  );

  return {
    activeNamedOrg,
    activeGithubOrgIsEnterpriseServer:
      connectedGithubOrgs?.instance_is_enterprise_server ?? false,
    isOrgAdmin,
    activeGithubOrgBlocked:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.blocked ?? false,
    // The backend sets is_personal_org from this same GitHub account type.
    activeGithubOrgIsPersonal:
      connectedGithubOrgs?.installations.find(
        (installation) =>
          installation.account.login.toLowerCase() === data?.toLowerCase(),
      )?.account.type === "User",
    activeGithubOrgHasRunFirstJob:
      (isImpersonating ||
        connectedGithubOrgs?.installations.find(
          (installation) => installation?.account?.login === data,
        )?.has_run_first_job) ??
      false,
    activeGithubOrgHasRunIncrementalDockerBuild:
      (isImpersonating ||
        connectedGithubOrgs?.installations.find(
          (installation) => installation?.account?.login === data,
        )?.has_run_incremental_docker_build) ??
      false,
    bazelCacheDashboardEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.bazel_cache_dashboard_enabled ??
        false),
    networkDashboardEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.network_dashboard_enabled ??
        false),
    globalNetworkMonitoringEnabled:
      user?.is_superuser === true ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.global_network_monitoring_enabled ??
        false),
    // codesmith_enabled is the resolved value from the backend
    // (GithubAppInstallation::isCodesmithEnabled), which folds in suspended
    // state but deliberately not `blocked`: blocked orgs still see the
    // Codesmith UI, and usage is gated via activeGithubOrgBlocked +
    // backend admission.
    codesmithEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_enabled ??
        false),
    // codesmithEnabled falls back to false when org data is missing (query
    // error, or the active org isn't in installations), so callers that must
    // distinguish "off" from "unknown" (e.g. the Codesmith rail row) check
    // this alongside it.
    codesmithEnabledKnown:
      isImpersonating ||
      connectedGithubOrgs?.installations.some(
        (installation) => installation?.account?.login === data,
      ) === true,
    // GithubAppInstallation::codesmithNeedsOrganization: a personal account
    // that sees [code]smith's Home tile and "needs an organization" page
    // while codesmithEnabled stays false.
    codesmithNeedsOrganization:
      connectedGithubOrgs?.installations.find(
        (installation) =>
          installation.account.login.toLowerCase() === data?.toLowerCase(),
      )?.codesmith_needs_organization ?? false,
    // codesmith_automations_enabled is the resolved value from the backend
    // (GithubAppInstallation::isCodesmithAutomationsEnabled).
    codesmithAutomationsEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_automations_enabled ??
        false),
    // codesmith_projects_enabled is the resolved value from the backend
    // (GithubAppInstallation::isCodesmithProjectsEnabled). No impersonation
    // bypass: the projects API resolves the rollout for the impersonating
    // viewer and 404s when the org is not enrolled.
    codesmithProjectsEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_projects_enabled ?? false,
    // No impersonation bypass: the proofs listing gates on the org rollout.
    proofEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.proof_enabled ?? false,
    proofRecordingChaptersEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_proof_recording_chapters_enabled ?? false,
    codesmithProofSettingsEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_proof_settings_enabled ??
        false),
    // Gates the monitor "Fix with Codesmith" button. Same flag that gates
    // backend per-VM key provisioning, so the button only appears when the
    // SSH-debug feature is actually live for the org.
    vmDebugSshEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_vm_debug_ssh_enabled ??
        false),
    // Gates the PR-ready Slack DM feature (the per-user toggle and the
    // welcome DM). Backend mirrors the gate everywhere a DM could fire.
    codesmithPrReadyDmsEnabled:
      isImpersonating ||
      (connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_pr_ready_dms_enabled ??
        false),
    codesmithLeftSidebarRedesignEnabled:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.codesmith_left_sidebar_redesign_enabled ?? false,
    activeGithubOrgHitSecondaryRateLimit:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.hit_secondary_rate_limit ?? false,
    activeGithubOrgNextScaleApp:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.next_scale_app ?? null,
    activeGithubOrgReposWithoutAccess:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.repos_without_access ?? null,
    stickyDiskCacheAllowanceGb:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.stickydisk_cache_allowance_gb ?? null,
    // False while org data is missing, so callers that must not act on an
    // unresolved value check `isLoading` alongside it.
    activeGithubOrgHasPaymentMethod:
      connectedGithubOrgs?.installations.find(
        (installation) => installation?.account?.login === data,
      )?.has_payment_method ?? false,
    // True only while the initial connectedGithubOrgs fetch is in flight, so
    // callers can distinguish "org data hasn't resolved yet" from a resolved
    // `codesmithEnabled: false`.
    isLoading,
    // True once the query has resolved successfully. Unlike `!isLoading`,
    // stays false when the query errored, so callers can tell an
    // authoritative "no installations" (resolved, activeNamedOrg null)
    // apart from "unknown" (loading or failed fetch).
    orgsResolved: connectedGithubOrgs !== undefined,
    orgsError,
  };
}

export function useHasPaymentMethod() {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  const { data } = useQuery(
    orgQueryKey("hasPaymentMethod", activeNamedOrg),
    async () => {
      const response = await api.get(
        `${orgApiPath(activeNamedOrg)}/billing/has-payment-method`,
      );
      return response.data?.has_payment_method;
    },
    {
      suspense: true,
      enabled: !!activeNamedOrg,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      // Refetch when the user returns to the tab so paid-status gating
      // reflects a card added in the Stripe portal (a separate tab) without
      // a manual refresh, matching usePaymentMethodDisplay. Background
      // refetches don't re-suspend, so this is a silent update.
      refetchOnWindowFocus: true,
    },
  );

  return {
    hasPaymentMethod: data,
  };
}

// dateRangeAtom starts off as "Month to Date" in UTC
export const dateRangeAtom = atom<DateRange>({
  from: DateTime.now()
    .setZone("utc")
    .startOf("month")
    .startOf("day")
    .toJSDate(),
  to: DateTime.now().setZone("utc").endOf("day").toJSDate(),
});

// startDateAtom is a derived read-only atom that returns the start date of the current date range in UTC.
// If the start date is not set, it returns today's date in UTC.
export const startDateAtom = atom<Date | undefined>((get) => {
  const dateRange = get(dateRangeAtom);
  return (
    dateRange?.from ?? DateTime.now().setZone("utc").startOf("day").toJSDate()
  );
});

// endDateAtom is a derived atom that returns the end date of the current date range in UTC.
// If the end date is not set, it returns the start date plus one day in UTC.
export const endDateAtom = atom<Date | undefined>((get) => {
  const dateRange = get(dateRangeAtom);
  const startDate = get(startDateAtom);
  if (!dateRange?.to && startDate) {
    return DateTime.fromJSDate(startDate)
      .setZone("utc")
      .endOf("day")
      .toJSDate();
  }
  return dateRange?.to;
});

export async function prefetchDailyMetrics(
  queryClient: QueryClient,
  api: AxiosInstance,
  org: NamedOrg,
  dateRange: DateRange,
) {
  const startDate = dateRange.from;
  const endDate = dateRange.to;
  const queryKey = [
    ...orgQueryKey("dailyMetrics", org),
    JSON.stringify(dateRange.from),
    JSON.stringify(dateRange.to),
  ];

  await queryClient.prefetchQuery(
    queryKey,
    async () => {
      return fetchDailyMetrics(api, org, {
        from: startDate!,
        to: endDate!,
      });
    },
    {
      staleTime: 30 * 1000,
    },
  );
}

export async function fetchDailyMetrics(
  api: AxiosInstance,
  org: NamedOrg | null,
  dateRange: DateRange,
) {
  const startDate = dateRange.from;
  const endDate = dateRange.to;
  const response = await api.get(
    `${orgApiPath(org)}/metrics/daily?start_date=${startDate?.toISOString()}&end_date=${endDate?.toISOString()}`,
  );
  const dailyMetrics: DailyMetrics = DailyMetrics.parse(response.data);
  return dailyMetrics.daily_metrics;
}

export function useDailyMetrics(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  const [startDate, setStartDate] = useAtom(startDateAtom);
  const [endDate, setEndDate] = useAtom(endDateAtom);

  const queryKey = useMemo(
    () => [
      ...orgQueryKey("dailyMetrics", activeNamedOrg),
      JSON.stringify(startDate),
      JSON.stringify(endDate),
    ],
    [activeNamedOrg, startDate, endDate],
  );
  const { data, isLoading } = useQuery(
    queryKey,
    async () => {
      return fetchDailyMetrics(api, activeNamedOrg, {
        from: startDate!,
        to: endDate!,
      });
    },
    {
      suspense: true,
      retry: 1,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      staleTime: 30 * 1000, // 30 seconds
      keepPreviousData: true,
      enabled: enabled && !!activeNamedOrg,
    },
  );

  return {
    dailyMetrics: data,
    isLoading,
  };
}

export async function prefetchTotalMetrics(
  queryClient: QueryClient,
  api: AxiosInstance,
  org: NamedOrg,
  dateRange: DateRange,
) {
  const startDate = dateRange.from;
  const endDate = dateRange.to;
  const queryKey = [
    ...orgQueryKey("totalMetrics", org),
    JSON.stringify(dateRange.from),
    JSON.stringify(dateRange.to),
  ];

  await queryClient.prefetchQuery(
    queryKey,
    async () => {
      return fetchTotalMetrics(api, org, {
        from: startDate!,
        to: endDate!,
      });
    },
    {
      staleTime: 30 * 1000,
    },
  );
}

export async function fetchTotalMetrics(
  api: AxiosInstance,
  org: NamedOrg | null,
  dateRange: DateRange,
) {
  const startDate = dateRange.from;
  const endDate = dateRange.to;
  const response = await api.get(
    `${orgApiPath(org)}/metrics/total?start_date=${startDate?.toISOString()}&end_date=${endDate?.toISOString()}`,
  );
  const totalMetrics: TotalMetrics = TotalMetrics.parse(response.data);

  return totalMetrics;
}

export function useTotalMetrics(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  const [startDate, setStartDate] = useAtom(startDateAtom);
  const [endDate, setEndDate] = useAtom(endDateAtom);

  const queryKey = useMemo(
    () => [
      ...orgQueryKey("totalMetrics", activeNamedOrg),
      JSON.stringify(startDate),
      JSON.stringify(endDate),
    ],
    [activeNamedOrg, startDate, endDate],
  );
  const { data, isFetching } = useQuery(
    queryKey,
    async () => {
      return fetchTotalMetrics(api, activeNamedOrg, {
        from: startDate!,
        to: endDate!,
      });
    },
    {
      suspense: true,
      retry: 1,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      staleTime: 30 * 1000, // 30 seconds
      keepPreviousData: true,
      enabled: enabled && !!activeNamedOrg,
    },
  );

  return {
    totalMetrics: data,
    isFetching,
  };
}

const InvoiceAmount = z.object({
  amount: z.number(),
  discountPercentage: z.number().optional(),
});

type InvoiceAmount = z.infer<typeof InvoiceAmount>;

export function useInvoiceAmount() {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  const queryKey = useMemo(
    () => orgQueryKey("invoiceAmount", activeNamedOrg),
    [activeNamedOrg],
  );
  const { data } = useQuery(
    queryKey,
    async () => {
      const response = await api.get(
        `${orgApiPath(activeNamedOrg)}/metrics/invoice-amount`,
      );
      const invoiceAmount: InvoiceAmount = InvoiceAmount.parse(response.data);
      return invoiceAmount;
    },
    {
      suspense: true,
      retry: 1,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      enabled: !!activeNamedOrg,
      staleTime: 30 * 1000, // 30 seconds
    },
  );

  return {
    invoiceAmount: data?.amount ?? 0,
    discountPercentage: data?.discountPercentage ?? 0,
  };
}

export const EmailAlertThreshold = z.object({
  threshold: z.union([z.string(), z.number()]).transform(Number).nullable(),
});

type EmailAlertThreshold = z.infer<typeof EmailAlertThreshold>;

export function useEmailAlertThreshold() {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => orgQueryKey("emailAlertThreshold", activeNamedOrg),
    [activeNamedOrg],
  );
  const { data } = useQuery(
    queryKey,
    async () => {
      const response = await api.get(
        `${orgApiPath(activeNamedOrg)}/email-alert-threshold`,
      );
      const emailAlertThreshold: EmailAlertThreshold =
        EmailAlertThreshold.parse(response?.data);
      return emailAlertThreshold;
    },
    {
      suspense: true,
      enabled: !!activeNamedOrg,
      retry: 1,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      // NB: We want the latest email alert threshold to be used in case the user
      // navigates away from the settings page and then comes back.
      cacheTime: 0,
    },
  );

  const {
    mutate,
    isLoading,
    data: mutateResponse,
  } = useMutation(
    async (threshold: number) => {
      return await api.put(
        `${orgApiPath(activeNamedOrg)}/email-alert-threshold`,
        {
          threshold: threshold,
        },
      );
    },
    {
      onMutate: (threshold) => {
        queryClient.setQueryData(queryKey, threshold);
      },
      onSuccess: () => {
        toast.success("Successfully updated email alert threshold", {
          duration: 2000,
        });
      },
      onError: () => {
        toast.error("Failed to update email alert threshold");
      },
    },
  );

  return {
    emailAlertThreshold:
      mutateResponse?.data?.threshold ?? data?.threshold ?? 0,
    setEmailAlertThreshold: mutate,
    emailAlertThresholdLoading: isLoading,
  };
}

const PrimaryEmail = z.object({
  primary_email: z.string().nullable(),
});

type PrimaryEmail = z.infer<typeof PrimaryEmail>;

export function usePrimaryEmail(options?: { suspense?: boolean }) {
  const suspense = options?.suspense ?? true;
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => orgQueryKey("primaryEmail", activeNamedOrg),
    [activeNamedOrg],
  );
  const { data, isLoading: primaryEmailFetching } = useQuery(
    queryKey,
    async () => {
      const response = await api.get(
        `${orgApiPath(activeNamedOrg)}/primary-email`,
      );
      const primaryEmail: PrimaryEmail = PrimaryEmail.parse(response.data);
      return primaryEmail;
    },
    {
      suspense,
      retry: 1,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      enabled: !!activeNamedOrg,
      // NB: We want the latest primary email to be used in case the user
      // navigates away from the settings page and then comes back.
      cacheTime: 0,
    },
  );

  const {
    mutate,
    isLoading,
    data: mutateResponse,
  } = useMutation(
    async (primaryEmail: string) => {
      return await api.put(`${orgApiPath(activeNamedOrg)}/primary-email`, {
        primary_email: primaryEmail,
      });
    },
    {
      onMutate: (primaryEmail) => {
        queryClient.setQueryData(queryKey, { primary_email: primaryEmail });
      },
    },
  );

  return {
    primaryEmail:
      mutateResponse?.data?.primary_email ?? data?.primary_email ?? undefined,
    setPrimaryEmail: mutate,
    /** True while PUT /primary-email is in flight */
    primaryEmailLoading: isLoading,
    /** True while GET /primary-email is in flight */
    primaryEmailFetching,
  };
}

async function fetchIsPersonalOrg(
  api: AxiosInstance,
  org: NamedOrg | null,
): Promise<boolean> {
  const response = await api.get(`${orgApiPath(org)}/is-personal-org`);
  // TODO(aayush): We could type check this response, but doesn't seem to be necessary.
  return response.data?.is_personal_org ?? false;
}

export function useIsPersonalOrg() {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  const { data } = useQuery(
    orgQueryKey("isPersonalOrg", activeNamedOrg),
    () => fetchIsPersonalOrg(api, activeNamedOrg),
    {
      suspense: true,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      staleTime: 30 * 1000, // 30 seconds
      enabled: !!activeNamedOrg,
    },
  );

  return {
    isPersonalOrg: data,
  };
}

const TeamMember = z.object({
  login: z.string().nullable(),
  avatar_url: z.string().nullable(),
  // "ADMIN" (a.k.a org owner) vs "MEMBER" – older backend versions may still send OWNER so we accept that too.
  role: z.enum(["ADMIN", "OWNER", "MEMBER"]).optional(),
  has_account: z.boolean().optional(),
});

const TeamMembersNeedPerms = z.object({
  needPerms: z.boolean(),
});

const TeamMembersList = z.array(TeamMember);

export type TeamMember = z.infer<typeof TeamMember>;

export function useTeamMembers() {
  const api = useApi();
  const { activeNamedOrg } = useActiveGithubOrgForUser();

  const { data } = useQuery(
    orgQueryKey("teamMembers", activeNamedOrg),
    async () => {
      const response = await api.get(`${orgApiPath(activeNamedOrg)}/team`);
      const result = TeamMembersNeedPerms.safeParse(response.data);
      if (result.success) {
        return { needPerms: result.data.needPerms, teamMembers: null };
      } else {
        const teamMembers: TeamMember[] = TeamMembersList.parse(response.data);
        return { needPerms: false, teamMembers };
      }
    },
    {
      suspense: true,
      useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404,
      staleTime: 30 * 1000, // 30 seconds
      enabled: !!activeNamedOrg,
    },
  );

  return {
    needPerms: data?.needPerms,
    teamMembers: data?.teamMembers,
  };
}

const HeardFrom = z.object({
  success: z.boolean(),
});

type HeardFromSource = {
  heard_from: string;
  heard_from_other: string | null;
};

/**
 * Hook for setting how a user heard about Blacksmith
 * This is used in the onboarding process before the GitHub app installation
 */
export function useHeardFrom() {
  const api = useApi();
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError, isSuccess } = useMutation(
    async (source: HeardFromSource) => {
      const response = await api.post("/user/heard-from", {
        heard_from: source.heard_from,
        heard_from_other: source.heard_from_other,
      });

      return HeardFrom.parse(response.data);
    },
    {
      onError: () => {
        // Silently fail - we don't want to block the onboarding process
        console.error("Failed to save heard from information");
      },
    },
  );

  return {
    setHeardFrom: mutate,
    isLoading,
    isError,
    isSuccess,
  };
}

export function useOrgUrlSync() {
  const pageRouter = usePageRouter();
  // Use non-suspense version to avoid blocking page render while waiting for orgs
  // The sync will happen asynchronously when the data arrives
  const { connectedGithubOrgs } = useConnectedGithubOrgsNonSuspense();
  const { user } = useContext(AuthContext);
  const orgsQueryKey = connectedGithubOrgsQueryKey(
    connectedGithubOrgs?.instance_slug ??
      urlInstanceSlug(undefined, user?.default_instance_slug),
  );
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  // GitHub Actions filter atoms
  const [, setGHAOrgChartFilters] = useAtom(ghaOrgChartFiltersAtom);
  const [, setGHARepoChartFilters] = useAtom(ghaRepoChartFiltersAtom);
  const [, setGHAOrgRepoTableFilters] = useAtom(ghaOrgRepoTableFiltersAtom);
  const [, setGHARepoJobTableFilters] = useAtom(ghaRepoJobTableFiltersAtom);

  // Docker filter atoms
  const [, setDockerOrgChartFilters] = useAtom(dockerOrgChartFiltersAtom);
  const [, setDockerRepoChartFilters] = useAtom(dockerRepoChartFiltersAtom);
  const [, setDockerOrgTableFilters] = useAtom(dockerOrgTableFiltersAtom);
  const [, setDockerRepoTableFilters] = useAtom(dockerRepoTableFiltersAtom);

  // Workflow runs filter atoms
  const [, setRunsOrgFilters] = useAtom(runsOrgFiltersAtom);

  const urlOrg = pageRouter.query.org as string | undefined;

  const { mutate } = useMutation(
    async (orgName: string) => {
      const instanceSlug = orgInstanceSlug(
        connectedGithubOrgs,
        orgName,
        user?.is_superuser,
      );
      if (!instanceSlug) throw new Error("GitHub instance is not resolved");
      const response = await api.post(
        `/user/${encodeURIComponent(instanceSlug)}/orgs`,
        {
          org_name: orgName,
        },
      );
      return response.data;
    },
    {
      mutationKey: SWITCH_ACTIVE_ORG_MUTATION_KEY,
      onMutate: async (orgName) => {
        await queryClient.cancelQueries(orgsQueryKey);
        assertDefined(
          connectedGithubOrgs,
          "GitHub organizations data is undefined during org switch",
        );

        queryClient.setQueryData(orgsQueryKey, {
          ...(connectedGithubOrgs ?? { installations: [] }),
          active_org_name: orgName,
          // Preserve the current is_org_admin value during optimistic update
          is_org_admin: connectedGithubOrgs?.is_org_admin ?? false,
        });

        identifyUserForOrg(posthog, user, orgName);

        // Clear all filters when switching organizations
        // GHA filters
        setGHAOrgChartFilters(defaultOrgChartFilters);
        setGHARepoChartFilters(defaultRepoChartFilters);
        setGHAOrgRepoTableFilters({});
        setGHARepoJobTableFilters({});

        // Docker filters
        setDockerOrgChartFilters(defaultDockerOrgChartFilters);
        setDockerRepoChartFilters(defaultDockerRepoChartFilters);
        setDockerOrgTableFilters({});
        setDockerRepoTableFilters({});

        // Workflow runs filters
        setRunsOrgFilters(defaultOrgFilters);
      },
      onSuccess: async (data) => {
        // Update the cache with the server response data.
        // NB: We need to do this here because the optimistic update in onMutate doesn't know
        // the true value of is_org_admin for the new org being switched to.
        queryClient.setQueryData(orgsQueryKey, (prev: any) => ({
          ...(prev ?? { installations: [] }),
          active_org_name: data.active_org_name,
          active_instance: data.active_instance,
          is_org_admin: data.is_org_admin,
        }));

        // Clear assistant session cache after the server has confirmed the org switch,
        // so the refetch triggered by the active useQuery observer returns sessions
        // for the new org (not the old one).
        queryClient.removeQueries(ASSISTANT_SESSIONS_QUERY_KEY);
        queryClient.invalidateQueries("user");
      },
      onError: (err, orgName) => {
        toast.error("Failed to set active org");
      },
    },
  );

  // Sync URL org with server-side active org when URL changes
  useEffect(() => {
    // Only run on client side, not during SSR/SSG
    if (typeof window === "undefined") return;

    // Skip sync for internal-monitors pages - the org in the URL is the target org
    // for monitoring, not the user's active org selection
    if (pageRouter.pathname.startsWith("/admin/internal-monitors")) return;

    if (
      urlOrg &&
      connectedGithubOrgs &&
      user &&
      connectedGithubOrgs.active_org_name !== urlOrg
    ) {
      // Verify the org exists in the user's connected orgs
      const orgExists =
        connectedGithubOrgs.installations.some(
          (installation) => installation.account.login === urlOrg,
        ) || user.is_superuser;

      if (orgExists) {
        // Update the server-side active org to match the URL
        mutate(urlOrg);
      }
    }
  }, [urlOrg, connectedGithubOrgs, user, mutate, pageRouter.pathname]);
}
